import React, { FC, useMemo } from 'react';

import { Typography } from '@mui/material';
import { OrderShort } from '@solidgate/react-sdk';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import Api from '~/Api';
import SecuredSystemsV2Img from '~/components/organisms/SolidCheckout/assets/secured_systems_v2.webp';
import PaymentFormV1 from '~/components/organisms/SolidCheckout/components/PaymentForm/V1';
import PaymentFormV1Sidekicker from '~/components/organisms/SolidCheckout/components/PaymentForm/V1/sidekicker';
import { PaymentApi } from '~/components/organisms/SolidCheckout/types';
import { useIsViewport, Viewports } from '~/hooks/useIsViewport';
import projectConfig, { PROJECT } from '~/projects/config';
import analytics from '~/services/analytics';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { Typographies } from '~/theme/typography';
import { AnalyticPaymentMethods, PAYMENT_METHODS } from '~/types/payment';
import { ProductType } from '~/types/product';

import styles from './styles.module.scss';

type Props = {
  product: ProductType;
  onSuccessPayment: () => void | Promise<void>;
  onFormReady: () => void;
  purchaseSource?: string;
};

const PaymentForm: FC<Props> = ({
  product,
  onSuccessPayment,
  onFormReady,
  purchaseSource,
}) => {
  const { id, email } = useAppSelector((state) => state.profile);

  const { t } = useTranslation('general');
  const isDesktop = useIsViewport(Viewports.EXTRA_SMALL_DESKTOP);

  const handlePaymentSuccess = async (order: OrderShort): Promise<void> => {
    analytics.trackPurchase('platform payment - success', {
      user_id: id,
      value: order.amount ? order.amount / 100 : 0,
      product_id: product.id,
      product_name: product.name,
      order_id: order.order_id ? order.order_id : '',
      ...(purchaseSource ? { purchase_source: purchaseSource } : {}),
      currency: order.currency,
      email,
      transaction_id: product.id,
    });

    await onSuccessPayment();
  };

  const handlePaymentFail = (
    message: string,
    method: AnalyticPaymentMethods,
  ): void => {
    analytics.trackEvent('platform checkout - error', {
      product_id: product.id,
      product_name: product.name,
      method,
      error: message,
      ...(purchaseSource ? { purchase_source: purchaseSource } : {}),
    });
  };

  const handleCheckoutSubmit = (method: AnalyticPaymentMethods): void => {
    analytics.trackEvent('platform checkout - submit', {
      product_id: product.id,
      product_name: product.name,
      method,
      ...(purchaseSource ? { purchase_source: purchaseSource } : {}),
    });
  };

  const handlePaymentMethodChange = (type: PAYMENT_METHODS): void => {
    analytics.trackEvent('platform checkout - navigation', {
      method: type,
      ...(purchaseSource ? { purchase_source: purchaseSource } : {}),
    });
  };

  const isSidekicker = projectConfig.project === PROJECT.SIDEKICKER;

  const paymentApi: PaymentApi = useMemo(
    () => ({
      createPaymentIntentCard: (geo_country) =>
        Api.createPaymentIntentCard(id, product.id, geo_country),
      createPaymentIntentPaypal: isSidekicker
        ? undefined
        : (): ReturnType<
            NonNullable<PaymentApi['createPaymentIntentPaypal']>
          > => Api.createPaymentIntentPaypal(id, product.id),
    }),
    [id, isSidekicker, product.id],
  );

  const PaymentFormComponent = isSidekicker
    ? PaymentFormV1Sidekicker
    : PaymentFormV1;

  return (
    <div
      className={clsx(styles.container, { [styles.sidekicker]: isSidekicker })}
    >
      {!isDesktop && !isSidekicker && (
        <Typography
          className={styles.title}
          variant={Typographies.TITLE_MEDIUM}
          component="h3"
        >
          {t(
            'subscription:payment_form.select_payment_method',
            'Select your payment method',
          )}
        </Typography>
      )}

      <PaymentFormComponent
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentFail={handlePaymentFail}
        onPaymentSubmit={handleCheckoutSubmit}
        onPaymentMethodChange={handlePaymentMethodChange}
        onFormReady={onFormReady}
        paymentApi={paymentApi}
      />

      <div className={styles.guarantee}>
        <img
          src={SecuredSystemsV2Img}
          alt=""
          className={styles.guarantee__img}
        />

        <Typography
          className={styles.guarantee__text}
          variant={Typographies.LABEL_MEDIUM}
          component="h3"
        >
          {t('payments_secured', 'Payments secured')}
        </Typography>
      </div>
    </div>
  );
};

export default PaymentForm;
